import {FIELD_EAGLES} from '../../configs/fieldAmbience';

/**
 * Орел, кружащий над горой. Чистые функции без Phaser и DOM — по этому
 * описанию орла каждый кадр ставит сцена (см. scenes/FieldAmbience).
 *
 * Орел проступает из вышины над горой, делает несколько кругов и уходит
 * обратно. Чтобы два орла над разными горами не кружили как под копирку, путь
 * бывает нескольких видов — круг, овал, восьмерка, смещающаяся петля, — а у
 * каждого полета свои радиус, наклон, сторона обхода, начальная точка и темп.
 *
 * Время в расчет приходит снаружи (сколько орел уже кружит): положение —
 * функция времени, без накопления шагов. Пропавший кадр или вкладка, которую
 * скрыли и вернули, полет не ломают.
 *
 * В состояние партии случайность не попадает: антураж целиком визуальный, у
 * каждого клиента свой (детерминизм записей матчей не задет).
 */

export const EAGLE_PATH_KINDS = {
  CIRCLE: 'circle',
  ELLIPSE: 'ellipse',
  EIGHT: 'eight',
  ROSETTE: 'rosette',
};

const TWO_PI = Math.PI * 2;
// Шаг времени, по которому считается курс орла (куда он смотрит), мс
const HEADING_STEP_MS = 16;

const randomRange = (rng, min, max) => min + rng() * (max - min);
const smoothstep = (value) => {
  const share = Math.min(1, Math.max(0, value));
  return share * share * (3 - 2 * share);
};

/** Вид пути — по весам из конфига; `excludeKinds` — уже занятые другими орлами */
const pickPathKind = (rng, excludeKinds = []) => {
  const allKinds = Object.values(EAGLE_PATH_KINDS);
  const freeKinds = allKinds.filter((kind) => !excludeKinds.includes(kind));
  const kinds = freeKinds.length > 0 ? freeKinds : allKinds;
  const totalWeight = kinds.reduce((sum, kind) => sum + FIELD_EAGLES.paths[kind].weight, 0);
  let roll = rng() * totalWeight;
  for (const kind of kinds) {
    roll -= FIELD_EAGLES.paths[kind].weight;
    if (roll < 0) {
      return kind;
    }
  }
  return kinds[kinds.length - 1];
};

/**
 * Полет орла над горой.
 *
 * @param {Object} params
 * @param {{x: number, y: number}} params.center — центр ячейки с горой
 * @param {number} params.hexSize
 * @param {Function} [params.rng]
 * @param {string} [params.kind] — вид пути (EAGLE_PATH_KINDS); не задан — случайный
 * @param {string[]} [params.excludeKinds] — виды пути, которые сейчас у других
 *   орлов: новый берет другой, пока свободные есть
 * @returns {Object|null} описание полета; null — нет горы или размера ячейки
 */
export const buildEagleFlight = ({center, hexSize, rng = Math.random, kind, excludeKinds} = {}) => {
  if (!center || !(hexSize > 0)) {
    return null;
  }

  const config = FIELD_EAGLES;
  const pathKind = Object.values(EAGLE_PATH_KINDS).includes(kind) ? kind : pickPathKind(rng, excludeKinds);
  const path = config.paths[pathKind];
  const jitter = hexSize * config.centerJitterFactor;

  return {
    kind: pathKind,
    center: {
      x: center.x + randomRange(rng, -jitter, jitter),
      y: center.y + randomRange(rng, -jitter, jitter),
    },
    radius: hexSize * randomRange(rng, config.radiusFactor.min, config.radiusFactor.max) * (path.scale ?? 1),
    // Отношение малой оси пути к большой (у круга и петли — 1)
    ratio: path.ratio ? randomRange(rng, path.ratio.min, path.ratio.max) : 1,
    // Поворот всего пути вокруг горы
    tiltRad: rng() * TWO_PI,
    // Сторона обхода: по часовой стрелке или против
    direction: rng() < 0.5 ? 1 : -1,
    // С какой точки пути орел начинает
    phase: rng() * TWO_PI,
    loopMs: randomRange(rng, config.loopMs.min, config.loopMs.max),
    durationMs: randomRange(rng, config.durationMs.min, config.durationMs.max),
    fadeMs: config.fadeMs,
    sizeFactor: randomRange(rng, config.sizeFactor.min, config.sizeFactor.max),
    // Петля: насколько и с какого места радиус «дышит»
    wobble: path.wobble ? randomRange(rng, path.wobble.min, path.wobble.max) : 0,
    wobblePhase: rng() * TWO_PI,
    // Взмахи: как часто и с какого момента
    flapEveryMs: randomRange(rng, config.flap.everyMs.min, config.flap.everyMs.max),
    flapOffsetMs: rng() * config.flap.everyMs.max,
  };
};

/** Точка пути в момент времени — относительно центра круга, до поворота пути */
const resolvePathPoint = (flight, angle) => {
  const {kind, radius, ratio} = flight;
  if (kind === EAGLE_PATH_KINDS.EIGHT) {
    // Восьмерка: вдоль большой оси — один размах за оборот, поперек — два
    return {x: radius * Math.sin(angle), y: radius * ratio * Math.sin(angle * 2)};
  }
  if (kind === EAGLE_PATH_KINDS.ROSETTE) {
    // Радиус дышит полтора раза за оборот: петля замыкается через два оборота
    // и каждый раз проходит по новому месту
    const breathing = 1 + flight.wobble * Math.sin(angle * 1.5 + flight.wobblePhase);
    return {x: radius * breathing * Math.cos(angle), y: radius * breathing * Math.sin(angle)};
  }
  return {x: radius * Math.cos(angle), y: radius * ratio * Math.sin(angle)};
};

const resolvePlace = (flight, elapsedMs) => {
  const angle = flight.phase + flight.direction * TWO_PI * (elapsedMs / flight.loopMs);
  const point = resolvePathPoint(flight, angle);
  const cos = Math.cos(flight.tiltRad);
  const sin = Math.sin(flight.tiltRad);
  return {
    x: flight.center.x + point.x * cos - point.y * sin,
    y: flight.center.y + point.x * sin + point.y * cos,
  };
};

/** Размах крыльев: орел парит, изредка — пара неторопливых взмахов */
const resolveWingSpread = (flight, elapsedMs) => {
  const {depth, periodMs, count} = FIELD_EAGLES.flap;
  const sinceFlaps = (elapsedMs + flight.flapOffsetMs) % flight.flapEveryMs;
  if (sinceFlaps >= periodMs * count) {
    return 1;
  }
  const swing = Math.sin((Math.PI * sinceFlaps) / periodMs);
  return 1 - depth * swing * swing;
};

/**
 * Где орел и как он выглядит в момент времени.
 *
 * @param {Object} flight — полет из buildEagleFlight
 * @param {number} elapsedMs — сколько орел уже кружит
 * @returns {{x: number, y: number, rotation: number, wingSpread: number, visibility: number}}
 *   rotation — куда смотрит орел (радианы; 0 — вправо); wingSpread — доля
 *   полного размаха (1 — крылья расправлены); visibility — 0..1: орел
 *   проступает в начале и тает в конце
 */
export const resolveEaglePose = (flight, elapsedMs) => {
  const place = resolvePlace(flight, elapsedMs);
  const before = resolvePlace(flight, elapsedMs - HEADING_STEP_MS);
  const after = resolvePlace(flight, elapsedMs + HEADING_STEP_MS);
  const sinceStart = elapsedMs / flight.fadeMs;
  const untilEnd = (flight.durationMs - elapsedMs) / flight.fadeMs;

  return {
    x: place.x,
    y: place.y,
    rotation: Math.atan2(after.y - before.y, after.x - before.x),
    wingSpread: resolveWingSpread(flight, elapsedMs),
    visibility: smoothstep(Math.min(sinceStart, untilEnd)),
  };
};

/** Орел улетел */
export const checkIfEagleFlightDone = (flight, elapsedMs) => elapsedMs >= flight.durationMs;

/**
 * Через сколько появится орел.
 *
 * @param {Function} [rng]
 * @param {boolean} [isFirst] — первый орел после построения поля: его ждут меньше
 * @returns {number} миллисекунды
 */
export const resolveEagleDelayMs = (rng = Math.random, isFirst = false) => {
  const {min, max} = isFirst ? FIELD_EAGLES.firstDelayMs : FIELD_EAGLES.intervalMs;
  return randomRange(rng, min, max);
};
